import { LOG_LEVELS } from "./logger";
import { logger } from "$lib/utils/logger";
import { DateHelper } from "$lib/utils/dateHelper";

const APPSIGNAL_LOG_GROUP =
  import.meta.env.VITE_APPSIGNAL_LOG_GROUP || "frontend";
const UPLOAD_THRESHOLD = 20;
const UPLOAD_INTERVAL_MS = 30000;
const BUFFER_CAP = 100;
const LOG_INGEST_URL = import.meta.env.VITE_APPSIGNAL_FRONTEND_KEY
  ? `https://appsignal-endpoint.net/logs/json?api_key=${import.meta.env.VITE_APPSIGNAL_FRONTEND_KEY}`
  : null;

if (!LOG_INGEST_URL) {
  console.warn(
    "[AppsignalLogger] VITE_APPSIGNAL_FRONTEND_KEY not configured; remote logging disabled."
  );
}

interface LogEntry {
  timestamp: string;
  group: string;
  severity: string;
  message: string;
  attributes: Record<string, string | number | boolean>;
}

let displayId: string | null = null;
let entriesQueue: LogEntry[] = [];
let uploadTimeoutId: NodeJS.Timeout | null = null;
let isEnabled = false;
let isUploading = false;

function initializeAppsignalLogger({
  displayId,
  enabled,
}: {
  displayId: string;
  enabled: boolean;
}) {
  setDisplayId(displayId);
  setEnabled(enabled);
  console.info(
    "[AppsignalLogger] Initialized with displayId:",
    displayId,
    "enabled:",
    enabled
  );
}

function setDisplayId(newDisplayId: string): void {
  displayId = newDisplayId;
  console.debug(`[AppsignalLogger] displayId set to ${newDisplayId}`);
}

function setEnabled(enabled: boolean): void {
  if (!enabled) flush();
  isEnabled = enabled;
  if (enabled) void upload();
  console.debug(`[AppsignalLogger] Remote logging enabled: ${enabled}`);
}

// Errors JSON.stringify to "{}" (their props are non-enumerable), so format
// error-shaped args via toString. Shape check, not instanceof — errors can
// cross realms (iframes) and DOMException predates inheriting from Error.
function serializeArg(arg: unknown): string {
  if (typeof arg !== "object" || arg === null) return String(arg);
  const { name, message } = arg as { name?: unknown; message?: unknown };
  if (typeof name === "string" && typeof message === "string") {
    return `${name}: ${message}`;
  }
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg); // circular or otherwise unstringifiable
  }
}

function queueLogEntry(
  level: keyof typeof LOG_LEVELS,
  message: string,
  ...args: any[]
): void {
  const fullMessage =
    args.length > 0
      ? `${message} ${args.map(serializeArg).join(" ")}`
      : message;

  const logEntry: LogEntry = {
    timestamp: DateHelper.newDateWithServerOffset().toISOString(),
    group: APPSIGNAL_LOG_GROUP,
    severity: level,
    message: fullMessage,
    attributes: {},
  };

  entriesQueue.push(logEntry);
  if (entriesQueue.length > BUFFER_CAP) {
    entriesQueue.splice(0, entriesQueue.length - BUFFER_CAP);
  }
  if (entriesQueue.length >= UPLOAD_THRESHOLD) {
    void upload();
  } else {
    scheduleUpload();
  }
}

function canUpload(): boolean {
  return isEnabled && !!displayId && !!LOG_INGEST_URL;
}

function toNdjson(entries: LogEntry[]): string {
  const hostname = displayId as string;
  return entries
    .map((entry) => JSON.stringify({ ...entry, hostname }))
    .join("\n");
}

// fetch, not sendBeacon: Chrome 49 caps a page's lifetime sendBeacon payload
// at ~64 KB, silently rejecting every beacon after that. sendBeacon is
// reserved for flush() on unload.
async function upload(): Promise<boolean> {
  if (isUploading || !canUpload() || entriesQueue.length === 0) return false;
  clearUploadTimeout(); // this upload drains the whole queue — a timer is armed iff entries are waiting
  isUploading = true;
  const batch = entriesQueue;
  entriesQueue = [];
  let ok = true;
  try {
    await fetch(LOG_INGEST_URL!, {
      method: "POST",
      body: toNdjson(batch),
      mode: "no-cors",
    });
  } catch {
    ok = false;
    entriesQueue = batch.concat(entriesQueue);
    if (entriesQueue.length > BUFFER_CAP) {
      entriesQueue.splice(0, entriesQueue.length - BUFFER_CAP);
    }
    // raw console, never logger.* — logger re-enters the queue (feedback loop).
    // Lands in the device's local webengine.log even when remote logging is down.
    console.warn(
      `[AppsignalLogger] Upload failed; ${entriesQueue.length} entries retained`
    );
  } finally {
    isUploading = false;
  }
  if (ok && entriesQueue.length >= UPLOAD_THRESHOLD) {
    void upload(); // healthy transport: drain the mid-flight backlog now
  } else if (entriesQueue.length > 0) {
    scheduleUpload(); // failure or small backlog: wait out the interval
  }
  return ok;
}

function scheduleUpload(): void {
  if (uploadTimeoutId || !canUpload()) return;
  uploadTimeoutId = setTimeout(() => {
    uploadTimeoutId = null;
    void upload();
  }, UPLOAD_INTERVAL_MS);
}

function clearUploadTimeout(): void {
  if (uploadTimeoutId) {
    clearTimeout(uploadTimeoutId);
    uploadTimeoutId = null;
  }
}

// last-gasp on unload — sendBeacon survives page teardown (Chrome 49 has no
// fetch keepalive). The page is dying, so the queue clears regardless.
function flush(): void {
  clearUploadTimeout();
  if (!canUpload() || entriesQueue.length === 0) return;
  navigator.sendBeacon(LOG_INGEST_URL!, toNdjson(entriesQueue));
  entriesQueue = [];
}

function getQueueSize(): number {
  return entriesQueue.length;
}

function getEnabled(): boolean {
  return isEnabled;
}

// Wire capture at module load so logs from boot (pre-handshake, pre-config)
// are buffered. Upload eligibility is gated separately in upload().
logger.setCallback(queueLogEntry);

export {
  initializeAppsignalLogger,
  setDisplayId,
  setEnabled,
  queueLogEntry,
  getQueueSize,
  getEnabled,
  flush,
};
